import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  UseMutationResult,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import { keyBy, map, pick, flatMap, concat } from 'lodash';
import { ParsedQuery as PermissionSetQueryParams } from 'query-string';
import { RbacApi } from 'api/rbac';
import {
  CreateCustomPermissionSetPayload,
  PermissionNameMap,
  PermissionSetDefinition,
  PermissionSetDefinitionMap,
  PermissionSetDefinitionsLight,
  PermissionSetDefinitionsMapWithNames,
  Permissions,
  RbacPageSource,
  UpdatePermissionSetPayload,
} from 'api/rbac/rbac-types';
import { GENERAL_API_STALE } from 'config/query-config';
import { EntityAccessResponseMap } from 'contexts/permissions';

const PermissionSetDefinitionsKey = ['permission-set-definition'];

const useGetEntityAccessQueryKey = (
  entityIds: number[],
  pageSourceType: RbacPageSource,
) => {
  return ['entity-access', entityIds, pageSourceType];
};

type PermissionSetDefinitionsKeyType = typeof PermissionSetDefinitionsKey;

export const usePermissionSetDefinitionsQuery = (
  options?: UseQueryOptions<
    PermissionSetDefinition[],
    unknown,
    Record<number, PermissionSetDefinition>,
    PermissionSetDefinitionsKeyType
  >,
): UseQueryResult<Record<number, PermissionSetDefinition>> => {
  return useQuery<
    PermissionSetDefinition[],
    unknown,
    Record<number, PermissionSetDefinition>,
    PermissionSetDefinitionsKeyType
  >(
    PermissionSetDefinitionsKey,
    async () => {
      return RbacApi.getPermissionSetDefinitions();
    },
    options,
  );
};

type PermissionSetMapKeyType = ['permission-set-map', string];

// Function to generate the query key
const generatePermissionSetMapQueryKey = (
  extraQueryParams = {},
): PermissionSetMapKeyType => {
  return ['permission-set-map', JSON.stringify(extraQueryParams)];
};

const permissionsToMap = (permissions: Permissions): PermissionNameMap => {
  return Object.values(permissions).reduce(
    (permissionNameMap, resourcePermissions) => {
      return {
        ...permissionNameMap,
        ...keyBy(resourcePermissions, 'permissionName'),
      };
    },
    {},
  ) as PermissionNameMap;
};

export const usePermissionSetDefinitionsMapQuery = (
  extraQueryParams?: PermissionSetQueryParams,
  options?: UseQueryOptions<
    Record<number, PermissionSetDefinitionMap>,
    unknown,
    Record<number, PermissionSetDefinitionMap>,
    PermissionSetMapKeyType
  >,
): UseQueryResult<Record<number, PermissionSetDefinitionMap>> => {
  return useQuery<
    Record<number, PermissionSetDefinitionMap>,
    unknown,
    Record<number, PermissionSetDefinitionMap>,
    PermissionSetMapKeyType
  >(
    generatePermissionSetMapQueryKey(extraQueryParams),
    async () => {
      return RbacApi.getPermissionSetDefinitions(extraQueryParams).then(
        (permissionSets) => {
          return permissionSets.reduce(
            (
              permissionSetMap,
              { permissionSetId, permission, defaultProjectPermission },
            ) => {
              permissionSetMap[permissionSetId] = {
                defaultProjectPermission,
                permissionMap: permissionsToMap(permission),
              };

              return permissionSetMap;
            },
            {},
          );
        },
      );
    },
    options,
  );
};

export const usePermissionSetDefinitionsForCtxMap = (): UseQueryResult<
  Record<number, PermissionSetDefinitionMap>
> => {
  return usePermissionSetDefinitionsMapQuery(
    {
      inCurrentUserCtx: 'true',
    },
    {
      staleTime: Infinity,
    },
  );
};

const PermissionSetMapWithNameKey = ['permission-set-map-with-name'];

type PermissionSetMapWithNameKeyType = typeof PermissionSetMapWithNameKey;

const PermissionSetLightMapWithNameKey = ['permission-set-map-light'];

type PermissionSetLightMapWithNameKeyType =
  typeof PermissionSetLightMapWithNameKey;

export const usePermissionSetDefintionWithNamesMapQuery = (
  options?: UseQueryOptions<
    PermissionSetDefinitionsMapWithNames[],
    unknown,
    PermissionSetDefinitionsMapWithNames[],
    PermissionSetMapWithNameKeyType
  >,
): UseQueryResult<PermissionSetDefinitionsMapWithNames[]> => {
  return useQuery(
    PermissionSetMapWithNameKey,
    async () => {
      return RbacApi.getPermissionSetDefinitions().then((permissionSets) => {
        return map(permissionSets, (entry) => {
          const permissions = flatMap(entry.permission, (permissionList) => {
            return map(permissionList, (permission) => {
              return pick(permission, [
                'permissionName',
                'requestScope',
                'actionScopes',
              ]);
            });
          });

          if (entry.defaultProjectPermission) {
            const linkedPermissions = flatMap(
              permissionSets?.find(
                (permissionSet) =>
                  permissionSet.permissionSetId ===
                  entry.defaultProjectPermission,
              )?.permission,
              (permissionList) => {
                return map(permissionList, (permission) => {
                  return pick(permission, [
                    'permissionName',
                    'requestScope',
                    'actionScopes',
                  ]);
                });
              },
            );

            const defaultProjectPermissionSetDetails = permissionSets?.find(
              (permissionSet) =>
                permissionSet.permissionSetId ===
                entry.defaultProjectPermission,
            );

            return {
              permissionSetId: entry?.permissionSetId,
              permissionSetName: entry?.permissionSetName,
              permissionSetDescription: entry?.permissionSetDescription,
              permissions: concat(permissions, linkedPermissions),
              permissionSetSpecificTo: entry.permissionSetSpecificTo,
              resource: entry?.resource ?? 'PROJECT',
              default: entry.default,
              defaultProjectPermissionDetails: {
                permissionSetId:
                  defaultProjectPermissionSetDetails?.permissionSetId,
                permissionSetName:
                  defaultProjectPermissionSetDetails?.permissionSetName,
              },
              permissionSetType: entry?.permissionSetType,
            };
          }

          return {
            permissionSetId: entry?.permissionSetId,
            permissionSetName: entry?.permissionSetName,
            permissionSetDescription: entry?.permissionSetDescription,
            permissionSetSpecificTo: entry?.permissionSetSpecificTo,
            permissions,
            resource: entry?.resource ?? 'PROJECT',
            default: entry.default,
            permissionSetType: entry?.permissionSetType,
          };
        });
      });
    },
    options,
  );
};

export const usePermissionSetDefinitionLight = (
  options?: UseQueryOptions<
    PermissionSetDefinitionsLight[],
    unknown,
    PermissionSetDefinitionsLight[],
    PermissionSetLightMapWithNameKeyType
  >,
): UseQueryResult<PermissionSetDefinitionsLight[]> => {
  return useQuery(
    PermissionSetLightMapWithNameKey,
    async () => {
      return RbacApi.getPermissionSetDefinitionsLight().then(
        (permissionSets) => {
          return map(permissionSets, (entry) => {
            return {
              permissionSetId: entry?.permissionSetId,
              permissionSetName: entry?.permissionSetName,
              permissionSetDescription: entry?.permissionSetDescription,
              permissionSetSpecificTo: entry?.permissionSetSpecificTo,
              resource: entry?.resource ?? 'PROJECT',
              default: entry.default,
              permissionSetType: entry?.permissionSetType,
              permissionSetObjectType: entry?.permissionSetObjectType,
              defaultProjectPermission: entry?.defaultProjectPermission,
            };
          });
        },
      );
    },
    options,
  );
};

export const useCreateCustomPermissionSetMutation = (): UseMutationResult<
  PermissionSetDefinition[], // response type
  unknown,
  CreateCustomPermissionSetPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => RbacApi.createCustomPermissionSet(payload),
    onSuccess: () => {
      return queryClient.invalidateQueries(['permission-set-map-with-name']);
    },
  });
};

export const useUpdatePermissionSetMutation = (): UseMutationResult<
  any, // response type
  unknown,
  UpdatePermissionSetPayload[]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => RbacApi.updatePermissionSet(payload),
    onSuccess: () => {
      return queryClient.invalidateQueries(['permission-set-map-with-name']);
    },
  });
};

export const useGetEntityAccessQuery = (
  entityIds: number[],
  pageSourceType: RbacPageSource,
  queryOptions: UseQueryOptions<
    EntityAccessResponseMap,
    unknown,
    EntityAccessResponseMap,
    (string | number[])[]
  > = {},
): UseQueryResult<EntityAccessResponseMap> =>
  useQuery(
    useGetEntityAccessQueryKey(entityIds, pageSourceType),
    () => RbacApi.getEntityAccess(entityIds, pageSourceType),
    {
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );
